import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { toastService } from '../../../../utils/toastService';
import useDealerRegistrationForm from './hooks/useDealerRegistrationForm';
import DealerRegistrationForm from './components/DealerRegistrationForm';
import TabsView from '../customerAddressBrandPage/components/tabs/tabsView';
import { useGetDealerRegistrationByIdQuery, useSaveDealerRegistrationMutation, useUpdateDealerRegistrationMutation, useSendDealerRegistrationToApprovalMutation } from '../../../../api/endpoints/dealerRegistration';
import { useGetDealerApprovalByIdQuery, useApproveDealerApprovalMutation, useRejectDealerApprovalMutation, useReturnDealerApprovalMutation } from '../../../../api/endpoints/dealerApproval';
import { resolveApprovalStatusText, resolveFlowText, resolveRoleText, approvalStatusSeverity, camelizeKeys, ApprovalStatus, ChangeTargetType, ChangeOperation, buildTabChangeMap } from '../../../../utils/dealerApprovalEnums';
import ApprovalSteps from '../../dealerApproval/detail/components/ApprovalSteps';
import ApprovalHistory from '../../dealerApproval/detail/components/ApprovalHistory';
import ApprovalBlocks from '../../dealerApproval/detail/components/ApprovalBlocks';
import ActionDialog from '../../dealerApproval/detail/components/ActionDialog';
import TabChangeBadge from '../customerAddressBrandPage/components/tabs/TabChangeBadge';
import '../../dealerApproval/dealerApproval.scss';

const DealerRegistrationEditPage = () => {
    const { id, sapDealerId: sapDealerIdParam } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { th, getCurrentLanguageId } = useLanguage();
    const languageId = getCurrentLanguageId();

    const isNew = !id && !sapDealerIdParam;
    const fromApprovalInbox = location.state?.from === 'approval-inbox';
    const form = useDealerRegistrationForm();
    const { setFormData, applyStagedPayload, selectedBlock } = form;

    const detailQueryArgs = sapDealerIdParam ? { sapDealerId: sapDealerIdParam, languageId } : { id, languageId };
    const { data: detail, isLoading, isError, refetch } = useGetDealerRegistrationByIdQuery(detailQueryArgs, { skip: isNew });

    const recordSapDealerId = sapDealerIdParam || form.sapDealerId || detail?.sapDealerId;
    const anchorId = selectedBlock?.dealerAddressBrandId;

    const [saveRegistration, { isLoading: isSaving }] = useSaveDealerRegistrationMutation();
    const [updateRegistration, { isLoading: isUpdating }] = useUpdateDealerRegistrationMutation();
    const [sendToApproval, { isLoading: isSending }] = useSendDealerRegistrationToApprovalMutation();
    const [approve, { isLoading: isApproving }] = useApproveDealerApprovalMutation();
    const [reject, { isLoading: isRejecting }] = useRejectDealerApprovalMutation();
    const [ret, { isLoading: isReturning }] = useReturnDealerApprovalMutation();

    const [actionDialog, setActionDialog] = useState({ visible: false, mode: null });
    const isActionBusy = isApproving || isRejecting || isReturning;

    const canEdit = isNew || detail?.canEdit === true;
    const canApprove = !isNew && detail?.canApprove === true;
    const canReject = !isNew && (detail?.canReject === true || (canApprove && detail?.canReject !== false));
    const canReturn = !isNew && (detail?.canReturn === true || (canApprove && detail?.canReturn !== false));
    const readOnly = !canEdit;

    const hasApproval = !isNew && !!detail?.approvalId;

    const formStagedApplicable = detail?.overallStatus === ApprovalStatus.DRAFT || detail?.overallStatus === ApprovalStatus.RETURNED || detail?.overallStatus === ApprovalStatus.PENDING;

    const showRejectionBanner = !isNew && (detail?.overallStatus === ApprovalStatus.RETURNED || detail?.overallStatus === ApprovalStatus.REJECTED) && !!String(detail?.rejectionReason ?? '').trim();

    const { data: approvalDetail } = useGetDealerApprovalByIdQuery({ id: detail?.approvalId, languageId, dealerAddressBrandId: anchorId }, { skip: !hasApproval });

    const { stagedPayload, changedFields } = useMemo(() => {
        const changes = approvalDetail?.changes || [];
        const updateChange = changes.find((c) => c.targetType === ChangeTargetType.UNIFIED && c.operation === ChangeOperation.UPDATE);
        const insertChange = changes.find((c) => c.targetType === ChangeTargetType.UNIFIED && c.operation === ChangeOperation.INSERT);
        const change = updateChange || insertChange;
        if (!change) return { stagedPayload: null, changedFields: [] };
        let payload = null;
        let fields = [];
        try {
            payload = change.payloadJson ? camelizeKeys(JSON.parse(change.payloadJson)) : null;
        } catch {
            payload = null;
        }
        if (change.operation === ChangeOperation.UPDATE) {
            try {
                const cf = change.changedFieldsJson ? camelizeKeys(JSON.parse(change.changedFieldsJson)) : null;
                if (cf) fields = Object.keys(cf);
            } catch {
                fields = [];
            }
        }
        return { stagedPayload: payload, changedFields: fields };
    }, [approvalDetail]);

    useEffect(() => {
        if (detail && !isNew) {
            setFormData(detail);
            if (stagedPayload && formStagedApplicable) {
                applyStagedPayload(stagedPayload);
            }
        }
    }, [detail, isNew, setFormData, applyStagedPayload, stagedPayload, formStagedApplicable]);

    const formChangedFields = formStagedApplicable && !detail?.isNewRegistration ? changedFields : [];

    const mergedTabChanges = useMemo(() => detail?.tabChanges ?? approvalDetail?.tabChanges ?? [], [detail?.tabChanges, approvalDetail?.tabChanges]);
    const tabChangeMap = useMemo(() => buildTabChangeMap(mergedTabChanges), [mergedTabChanges]);
    const approvalBlocks = detail?.blocks ?? approvalDetail?.blocks ?? [];

    const dealer = useMemo(() => {
        if (!detail && !form.sapDealerId) return null;
        return { id: detail?.dealerId ?? detail?.sapDealerId ?? Number(form.sapDealerId), name: detail?.fullName ?? detail?.dealerName ?? form.fullName };
    }, [detail, form.sapDealerId, form.fullName]);

    const dealerAddress = useMemo(() => {
        if (!selectedBlock) return null;
        return { id: selectedBlock.siteUseId ?? selectedBlock.dealerAddressId, name: selectedBlock.addressName };
    }, [selectedBlock]);

    const brand = useMemo(() => selectedBlock?.selectedBrand || null, [selectedBlock]);

    const handleBack = () => {
        if (fromApprovalInbox) {
            navigate('/management/dealer-approval');
        } else {
            navigate('/management/dealer-registration');
        }
    };

    const warnIfInvalid = () => {
        form.setSubmitted(true);
        const errors = form.validate();
        if (errors.length) {
            toastService.warn(th(errors[0]));
            return true;
        }
        return false;
    };

    const handleSave = async () => {
        if (warnIfInvalid()) return;
        try {
            if (isNew) {
                const res = await saveRegistration(form.buildPayload(languageId)).unwrap();
                toastService.success(th('dealerRegistration.savedAsDraft'));
                const newSap = res?.data?.sapDealerId ?? form.sapDealerId;
                if (newSap) {
                    navigate(`/management/dealer-registration/dealer/${newSap}`);
                }
            } else {
                await updateRegistration(form.buildUpdatePayload(languageId)).unwrap();
                toastService.success(th('dealerRegistration.changesSaved'));
                refetch();
            }
        } catch {
            // Hata mesajı global baseQueryWithReauth tarafından gösteriliyor (Pending iken 409).
        }
    };

    const handleSendToApproval = () => {
        if (warnIfInvalid()) return;
        confirmDialog({
            header: th('dealerApproval.lockPopup.header'),
            message: th('dealerApproval.lockPopup.message'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: th('dealerApproval.lockPopup.accept'),
            rejectLabel: th('dealerApproval.lockPopup.reject'),
            accept: async () => {
                try {
                    const payload = form.buildUpdatePayload(languageId);
                    await sendToApproval({ sapDealerId: Number(recordSapDealerId), languageId, payload }).unwrap();
                    toastService.success(th('dealerApproval.success.submit'));
                    refetch();
                } catch {
                    // Hata mesajı global baseQueryWithReauth tarafından gösteriliyor.
                }
            }
        });
    };

    const openActionDialog = (mode) => setActionDialog({ visible: true, mode });
    const closeActionDialog = () => setActionDialog({ visible: false, mode: null });

    const handleActionConfirm = async (text) => {
        const approvalId = Number(detail?.approvalId);
        if (!approvalId) return;
        try {
            if (actionDialog.mode === 'approve') {
                await approve({ approvalId, note: text, languageId }).unwrap();
                toastService.success(th('dealerApproval.success.approve'));
            } else if (actionDialog.mode === 'reject') {
                await reject({ approvalId, reason: text, languageId }).unwrap();
                toastService.success(th('dealerApproval.success.reject'));
            } else if (actionDialog.mode === 'return') {
                await ret({ approvalId, note: text, languageId }).unwrap();
                toastService.success(th('dealerApproval.success.return'));
            }
            closeActionDialog();
            navigate('/management/dealer-approval');
        } catch {
            // Hata mesajı global baseQueryWithReauth tarafından gösteriliyor.
        }
    };

    if (!isNew && isLoading) {
        return <div className="card">{th('common.loading')}</div>;
    }
    if (!isNew && isError) {
        return <div className="card text-red-600">{th('common.error')}</div>;
    }

    return (
        <div className="card">
            <ConfirmDialog />
            <style>{`.da-timeline .p-timeline-event-opposite { flex: 0; padding: 0; }`}</style>

            <div className="flex flex-row align-items-center justify-content-between mb-3">
                <div className="flex align-items-center gap-3">
                    <Button icon="pi pi-arrow-left" text label={fromApprovalInbox ? th('dealerApproval.inboxTitle') : th('dealerRegistration.backToList')} onClick={handleBack} />
                    {!isNew && detail && (
                        <div className="flex align-items-center gap-2 flex-wrap">
                            <Tag value={resolveApprovalStatusText(th, detail.overallStatus)} severity={approvalStatusSeverity(detail.overallStatus)} />
                            {detail.flowKey && <Tag value={resolveFlowText(th, detail.flowKey)} severity={detail.flowKey === 'DEALER_PAYMENT' ? 'warning' : 'info'} />}
                            {detail.totalSteps > 0 && (
                                <span className="text-sm font-medium">
                                    {th('dealerApproval.stepSummary', {
                                        current: detail.currentStepOrder ?? detail.approvalStatus ?? 0,
                                        total: detail.totalSteps
                                    })}
                                </span>
                            )}
                            {detail.currentStepRole && <span className="text-sm">{resolveRoleText(th, detail.currentStepRole)}</span>}
                            {detail.isNewRegistration && <Tag value={th('dealerApproval.isNewRegistration')} severity="success" icon="pi pi-star" />}
                        </div>
                    )}
                </div>
                <div className="flex align-items-center gap-2">
                    {canEdit && <Button label={th('common.save')} icon="pi pi-check" onClick={handleSave} loading={isSaving || isUpdating} />}
                    {canEdit && !isNew && recordSapDealerId && <Button label={th('dealerRegistration.sendToApproval')} icon="pi pi-send" severity="warning" onClick={handleSendToApproval} loading={isSending} />}
                    {canApprove && <Button label={th('dealerApproval.approve')} icon="pi pi-check" severity="success" onClick={() => openActionDialog('approve')} disabled={isActionBusy} />}
                    {canReject && <Button label={th('dealerApproval.reject')} icon="pi pi-times" severity="danger" onClick={() => openActionDialog('reject')} disabled={isActionBusy} />}
                    {canReturn && <Button label={th('dealerApproval.return')} icon="pi pi-replay" severity="warning" onClick={() => openActionDialog('return')} disabled={isActionBusy} />}
                </div>
            </div>

            {((readOnly && !canApprove) || showRejectionBanner || formChangedFields.length > 0 || detail?.isNewRegistration) && (
                <div className="flex flex-column gap-2 mb-3">
                    {readOnly && !canApprove && <Message severity="warn" text={th('dealerApproval.locked')} className="w-full" />}
                    {detail?.isNewRegistration && <Message severity="success" text={th('dealerApproval.newRegistrationInfo')} className="w-full" />}
                    {showRejectionBanner && <Message severity="error" text={`${th('dealerRegistration.rejectionReasonLabel')}: ${detail.rejectionReason}`} className="w-full" />}
                    {formChangedFields.length > 0 && <Message severity="info" text={th('dealerRegistration.pendingChangesInfo')} className="w-full" />}
                </div>
            )}

            <Accordion multiple activeIndex={[0]} className="mb-4">
                <AccordionTab
                    header={
                        <h5 className="m-0 flex align-items-center">
                            {th('dealerRegistration.title')}
                            <TabChangeBadge tabChange={tabChangeMap[ChangeTargetType.UNIFIED]} />
                        </h5>
                    }
                >
                    <DealerRegistrationForm readOnly={readOnly} isNew={isNew} changedFields={formChangedFields} {...form} />
                </AccordionTab>
                {hasApproval && (
                    <AccordionTab header={<h5 className="m-0">{th('dealerApproval.processTitle')}</h5>}>
                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <ApprovalSteps steps={approvalDetail?.steps || []} currentStepOrder={approvalDetail?.currentStepOrder ?? detail?.currentStepOrder} />
                            </div>
                            <div className="col-12 md:col-6">
                                <ApprovalHistory history={approvalDetail?.history || []} />
                            </div>
                        </div>
                    </AccordionTab>
                )}
            </Accordion>

            {!isNew && <ApprovalBlocks blocks={approvalBlocks} />}

            {!isNew && anchorId && dealer && (
                <div aria-disabled={readOnly}>
                    <TabsView key={anchorId} dealerAddressBrandId={anchorId} dealer={dealer} dealerAddress={dealerAddress} brand={brand} tabChanges={selectedBlock?.tabChanges || []} readOnly={readOnly} approvalContext />
                </div>
            )}

            <ActionDialog visible={actionDialog.visible} mode={actionDialog.mode} onHide={closeActionDialog} onConfirm={handleActionConfirm} loading={isActionBusy} />
        </div>
    );
};

export default DealerRegistrationEditPage;
